/**
 * Permission utility functions for frontend
 */

/**
 * Platform operator: owns the deployment, not any tenant company. Gates the
 * platform screens (GeneralSettings, WhatsAppSettings) and keeps tenant-only
 * settings tabs out of the Super Admin's nav.
 * @param {Object} user
 * @returns {boolean}
 */
export const isSuperAdmin = (user) => user?.role?.name === 'Super Admin';

/**
 * Check if user has a specific permission
 * @param {Object} user - User object with role and permissions
 * @param {string} resource - Resource name (e.g., 'job_cards')
 * @param {string} action - Action name (e.g., 'update', 'create_only')
 * @returns {boolean}
 */
export const hasPermission = (user, resource, action) => {
  if (!user || !user.role) return false;

  // Super Admin has all permissions
  if (user.role.name === 'Super Admin') return true;

  const permissions = user.role.permissions || [];
  const actions = Array.isArray(action) ? action : [action];
  // 'manage' is a superset of any action on the same resource (mirrors the
  // backend checkPermission middleware), so the UI and the API agree.
  return permissions.some(
    (p) => p.resource === resource && (actions.includes(p.action) || p.action === 'manage')
  );
};

/**
 * Exact (resource, action) match — no 'manage' superset fallback. Mirrors the
 * backend's requireDataExportPermission (routes/dataExports.js): 'manage' on
 * 'company' is already granted to every tenant's Admin role by default, so
 * the ordinary hasPermission() would silently show this page (and its
 * requests, which the server would then 403) to every admin. The spec's
 * "granted to nobody by default" only holds if the UI gate matches the
 * server's exact-match check.
 * @param {Object} user
 * @param {string} resource
 * @param {string} action
 * @returns {boolean}
 */
export const hasExactPermission = (user, resource, action) => {
  if (!user || !user.role) return false;
  if (user.role.name === 'Super Admin') return true;
  const permissions = user.role.permissions || [];
  return permissions.some((p) => p.resource === resource && p.action === action);
};

/**
 * True if the user has ANY of the given [resource, action] permission pairs.
 * @param {Object} user
 * @param {Array<[string,string]>} pairs
 * @returns {boolean}
 */
export const hasAnyPermission = (user, pairs = []) =>
  pairs.some(([resource, action]) => hasPermission(user, resource, action));

/**
 * Check if user has read-only access (create_only permission without management permissions)
 * @param {Object} user - User object with role and permissions
 * @param {string} resource - Resource name (default: 'job_cards')
 * @returns {boolean}
 */
export const isReadOnly = (user, resource = 'job_cards') => {
  if (!user || !user.role) return false;
  
  // Super Admin is never read-only
  if (user.role.name === 'Super Admin') return false;
  
  const permissions = user.role.permissions || [];
  
  // Has create_only permission
  const hasCreateOnly = permissions.some(p => p.resource === resource && p.action === 'create_only');
  
  // Has management permissions (update, delete, assign, etc.)
  const hasManagePermission = permissions.some(p => 
    p.resource === resource && ['update', 'delete', 'assign', 'manage'].includes(p.action)
  );
  
  return hasCreateOnly && !hasManagePermission;
};

/**
 * Check if user can create job cards
 * @param {Object} user - User object with role and permissions
 * @returns {boolean}
 */
export const canCreateJobCards = (user) => {
  return hasPermission(user, 'job_cards', 'create') || 
         hasPermission(user, 'job_cards', 'create_only');
};

/**
 * Check if user can manage job cards (update, delete, assign)
 * @param {Object} user - User object with role and permissions
 * @returns {boolean}
 */
export const canManageJobCards = (user) => {
  if (!user || !user.role) return false;
  if (user.role.name === 'Super Admin') return true;
  
  return hasPermission(user, 'job_cards', 'update') ||
         hasPermission(user, 'job_cards', 'manage');
};

/**
 * Check if user can authorize delivering a vehicle on credit.
 * Resolves the DB-granted deliver_on_credit/execute permission (seeded for
 * Admin/Manager by default). Super Admin is always allowed via hasPermission.
 * @param {Object} user - User object with role and permissions
 * @returns {boolean}
 */
export const canDeliverOnCredit = (user) => {
  return hasPermission(user, 'deliver_on_credit', 'execute');
};

/**
 * A company-wide action (the server gates it with scope: 'company'). Hides the
 * control for a mixed-role user — /auth/me sends all_branches_allowed: false
 * only then; an absent flag means all branches. The server still decides.
 */
export const hasCompanyWidePermission = (user, resource, action = 'manage') => {
  if (!user) return false;
  if (user.role?.name === 'Super Admin') return true;
  if (user.all_branches_allowed === false) return false;
  return hasPermission(user, resource, action);
};
